import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1528698827591-e19ccd7bc23d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #004744;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #004744;
  font-style: italic;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const ImageSection = styled.div`
  margin: 4rem 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const StyledImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 1.5rem;
  color: white;
`;

const ImageTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #004744;
  font-style: italic;
`;

const ImageDescription = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const PopUpStore: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const features = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
        </svg>
      ),
      title: language === 'it' ? 'POSIZIONE STRATEGICA' : 'STRATEGIC LOCATION',
      description: language === 'it' 
        ? 'Gli immobili per affitti brevi sono spesso situati in zone strategiche delle città, rendendoli ideali per pop-up store temporanei che vogliono attirare clienti in un\'area specifica.'
        : 'Short-term rental properties are often located in strategic city areas, making them ideal for temporary pop-up stores looking to attract customers in a specific area.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
      ),
      title: language === 'it' ? 'FLESSIBILITÀ DI NOLEGGIO' : 'FLEXIBLE RENTAL',
      description: language === 'it' 
        ? 'I contratti di locazione per pop-up store sono flessibili, permettendo di noleggiare lo spazio per periodi che vanno da pochi giorni a diverse settimane o mesi, a seconda delle esigenze dell\'azienda.'
        : 'Lease agreements for pop-up stores are flexible, allowing you to rent the space for periods ranging from a few days to several weeks or months, depending on the company\'s needs.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      title: language === 'it' ? 'ATTRARRE NUOVI CLIENTI' : 'ATTRACT NEW CUSTOMERS',
      description: language === 'it' 
        ? 'I pop-up store in immobili per affitti brevi permettono di raggiungere nuovi mercati e clienti, esponendo il brand a un pubblico diversificato che potrebbe non visitare i tradizionali punti vendita.'
        : 'Pop-up stores in short-term rental properties allow you to reach new markets and customers, exposing the brand to a diverse audience that may not visit traditional retail outlets.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
      ),
      title: language === 'it' ? 'SICUREZZA E GESTIONE' : 'SECURITY AND MANAGEMENT',
      description: language === 'it' 
        ? 'Gli immobili per affitti brevi utilizzati come pop-up store sono gestiti in modo professionale, garantendo sicurezza e manutenzione adeguata durante tutto il periodo di utilizzo.'
        : 'Short-term rental properties used as pop-up stores are professionally managed, ensuring security and proper maintenance throughout the period of use.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
        </svg>
      ),
      title: language === 'it' ? 'PERSONALIZZAZIONE DELLO SPAZIO' : 'SPACE CUSTOMIZATION',
      description: language === 'it' 
        ? 'Gli spazi possono essere personalizzati secondo l\'identità e le esigenze del brand, creando un\'esperienza unica per i clienti e rafforzando il messaggio del marchio.'
        : 'Spaces can be customized according to the brand\'s identity and needs, creating a unique experience for customers and reinforcing the brand message.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      title: language === 'it' ? 'TECNOLOGIA E CONNETTIVITÀ' : 'TECHNOLOGY AND CONNECTIVITY',
      description: language === 'it' 
        ? 'Gli immobili sono dotati di connessione internet ad alta velocità e infrastrutture tecnologiche necessarie per supportare le attività di vendita, come sistemi di pagamento elettronico e WiFi per i clienti.'
        : 'Properties are equipped with high-speed internet connection and technological infrastructures necessary to support sales activities, such as electronic payment systems and WiFi for customers.'
    }
  ];
  
  const examples = [
    {
      image: 'https://images.unsplash.com/photo-1528698827591-e19ccd7bc23d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'BOUTIQUE TEMPORANEA DI MODA' : 'TEMPORARY FASHION BOUTIQUE',
      description: language === 'it'
        ? 'Un appartamento in centro trasformato in una boutique di moda esclusiva per il lancio di una nuova collezione.'
        : 'A downtown apartment transformed into an exclusive fashion boutique for the launch of a new collection.'
    },
    {
      image: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'SHOWROOM TECNOLOGICO' : 'TECHNOLOGY SHOWROOM',
      description: language === 'it'
        ? 'Un loft moderno utilizzato come showroom per presentare le ultime innovazioni tecnologiche in un ambiente intimo e accogliente.'
        : 'A modern loft used as a showroom to present the latest technological innovations in an intimate and welcoming environment.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Pop Up Store | J-O-H' : 'Pop Up Store | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Trasforma il tuo immobile in un pop-up store temporaneo per brand, esposizioni ed eventi esclusivi' 
            : 'Transform your property into a temporary pop-up store for brands, exhibitions and exclusive events'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/pop-up">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>POP UP STORE</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Gli immobili per affitti brevi possono trasformarsi in pop-up store temporanei, offrendo ai brand una soluzione innovativa e flessibile per esporre i propri prodotti, lanciare collezioni o organizzare eventi promozionali. Questi spazi unici creano esperienze memorabili per i clienti e generano un buzz significativo attorno al marchio.'
              : 'Short-term rental properties can be transformed into temporary pop-up stores, offering brands an innovative and flexible solution to display their products, launch collections or organize promotional events. These unique spaces create memorable experiences for customers and generate significant buzz around the brand.'}
          </Description>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>
                  {feature.icon}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
          
          <SectionTitle>
            {language === 'it' ? 'ESEMPI DI POP-UP STORE' : 'POP-UP STORE EXAMPLES'}
          </SectionTitle>
          
          <ImageSection>
            {examples.map((example, index) => (
              <ImageContainer key={index}>
                <StyledImage src={example.image} alt={example.title} />
                <ImageOverlay>
                  <ImageTitle>{example.title}</ImageTitle>
                  <ImageDescription>{example.description}</ImageDescription>
                </ImageOverlay>
              </ImageContainer>
            ))}
          </ImageSection>
          
          <Description>
            {language === 'it' 
              ? 'I pop-up store negli immobili per affitti brevi rappresentano una soluzione innovativa per i brand che cercano di creare esperienze di vendita uniche e temporanee. Offrendo flessibilità, posizioni strategiche e un\'atmosfera distintiva, questi spazi permettono di entrare in connessione con i clienti in modi nuovi e memorabili.'
              : 'Pop-up stores in short-term rental properties represent an innovative solution for brands looking to create unique and temporary sales experiences. Offering flexibility, strategic positions and a distinctive atmosphere, these spaces allow you to connect with customers in new and memorable ways.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default PopUpStore; 